import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/** Bedien-Konstante der Oberfläche (EXPERIENCE §4.1), keine fachliche Frist. */
export const UNDO_SECONDS = 10;

/**
 * Rückgängig-Einblendung (DESIGN `components.undo-toast`, EXPERIENCE §4.1): schwarze Pille unten mittig mit
 * Satz, Knopf „Rückgängig“ und dünnem Zeitstrich für 10 s;
 * der Zähler pausiert bei Hover und Fokus. Ansage `aria-live="polite"`; die Einblendung nimmt nie den Fokus. Am Handy
 * unten über die volle Breite minus Rand (EXPERIENCE §10.2), der Satz bricht um statt abgeschnitten zu werden.
 */
export function UndoToast({
  message,
  onUndo,
  onDone,
  busy = false,
  seconds = UNDO_SECONDS,
  until,
  onProgress,
}: {
  /** Satz; darf einen Link enthalten (Story 5.109: „… ansehen“). */
  message: ReactNode;
  /** Fehlt (`null`), ist die Einblendung nur eine Bestätigung ohne Knopf „Rückgängig“ (z. B. „Freigegeben“ mit Teillieferung). */
  onUndo: (() => void) | null;
  /** Zeit abgelaufen: Einblendung ausblenden. */
  onDone: () => void;
  /** Rückgängig läuft gerade (Knopf gesperrt). */
  busy?: boolean;
  seconds?: number;
  /** Hartes Ende (z. B. Frist des Servers): blendet dann aus, auch wenn der Zähler pausiert. */
  until?: Date;
  /** Restzeit in ms bei jeder Änderung (z. B. um ein Angebot über das Aushängen hinaus ablaufen zu lassen). */
  onProgress?: (leftMs: number) => void;
}) {
  const total = seconds * 1000;
  const [left, setLeft] = useState(total);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused || busy;
  const last = useRef<number | null>(null);
  const toast = useRef<HTMLDivElement>(null);
  const undo = useRef(onUndo);
  const done = useRef(onDone);
  useEffect(() => {
    undo.current = onUndo;
    done.current = onDone;
  }, [onUndo, onDone]);

  useEffect(() => {
    if (paused) {
      last.current = null;
      return;
    }
    const timer = window.setInterval(() => {
      const now = performance.now();
      const step = last.current === null ? 0 : now - last.current;
      last.current = now;
      setLeft((l) => Math.max(0, l - step));
    }, 100);
    return () => window.clearInterval(timer);
  }, [paused]);

  const progress = useRef(onProgress);
  useEffect(() => {
    progress.current = onProgress;
  }, [onProgress]);
  useEffect(() => {
    progress.current?.(left);
    if (left <= 0) done.current();
  }, [left]);

  useEffect(() => {
    if (!until) return;
    const timer = window.setTimeout(() => done.current(), Math.max(0, until.getTime() - Date.now()));
    return () => window.clearTimeout(timer);
  }, [until]);

  const trigger = useCallback(() => {
    if (!busy) undo.current?.();
  }, [busy]);
  const canUndo = onUndo !== null;

  return (
    <div
      ref={toast}
      role="status"
      aria-live="polite"
      data-testid="undo-toast"
      className="fixed bottom-6 left-[calc(50%_+_var(--content-left)/2_-_var(--content-gutter)/2)] z-50 -translate-x-1/2 overflow-hidden rounded-full bg-ink text-on-ink shadow-float max-tablet:inset-x-4 max-tablet:bottom-[calc(16px+var(--fixed-bar-h,0px))] max-tablet:translate-x-0 max-tablet:rounded-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <div className={`flex min-h-13 items-center gap-4 py-2 pl-6 max-tablet:pl-4 ${canUndo ? 'pr-2' : 'pr-6 max-tablet:pr-4'}`}>
        <span className="text-row whitespace-nowrap max-tablet:min-w-0 max-tablet:flex-1 max-tablet:whitespace-normal">{message}</span>
        {canUndo ? (
          <button
            type="button"
            className="h-9 flex-none cursor-pointer rounded-full px-4 text-row underline underline-offset-2 disabled:cursor-default disabled:opacity-60 pointer-coarse:h-11"
            disabled={busy}
            onClick={trigger}
          >
            Rückgängig
          </button>
        ) : null}
      </div>
      <div aria-hidden className="h-[2px] bg-on-ink/60" style={{ width: `${(left / total) * 100}%` }} />
    </div>
  );
}

export interface UndoOffer {
  /** Was geschehen ist, z. B. „Wiedervorlage erledigt“ (auch mit Link). */
  text: ReactNode;
  /** Macht die Aktion rückgängig; fehlt, ist es nur eine Bestätigung ohne Knopf „Rückgängig“. */
  undo?: () => Promise<void>;
  /** Hartes Ende des Angebots (z. B. Frist des Servers); Laufzeit = min(10 s, Zeit bis dahin). */
  until?: Date;
}

/**
 * Eine Rückgängig-Einblendung je Seite (Story 2.4, Aufgabenseite): `offer` zeigt sie nach einer Aktion, eine neue
 * ersetzt die alte; `element` rendert die `UndoToast`. Fehler beim Rückgängig gehen an `onError`.
 */
export function useUndo(onError: (err: unknown) => void): { offer: (o: UndoOffer) => void; dismiss: () => void; element: React.ReactNode } {
  const [shown, setShown] = useState<(UndoOffer & { key: number; seconds: number }) | null>(null);
  const [busy, setBusy] = useState(false);
  const next = useRef(1);
  const offer = useCallback((o: UndoOffer) => {
    // Schon abgelaufen: kein Rückgängig anbieten.
    if (o.until && o.until.getTime() <= Date.now()) {
      setShown(null);
      return;
    }
    const seconds = o.until ? Math.min(UNDO_SECONDS, (o.until.getTime() - Date.now()) / 1000) : UNDO_SECONDS;
    setShown({ ...o, key: next.current++, seconds });
  }, []);
  const close = useCallback(() => setShown(null), []);
  const run = useCallback(async () => {
    if (!shown?.undo) return;
    setBusy(true);
    try {
      await shown.undo();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
      setShown(null);
    }
  }, [shown, onError]);
  return {
    offer,
    dismiss: close,
    element: shown ? (
      <UndoToast
        key={shown.key}
        message={shown.text}
        busy={busy}
        onUndo={shown.undo ? () => void run() : null}
        onDone={close}
        seconds={shown.seconds}
        {...(shown.until ? { until: shown.until } : {})}
      />
    ) : null,
  };
}
